/**
 * Loop Diff — words for the parts the database function returns.
 *
 * The comparison itself is computed by `catchball_loop_diff` (migration 822)
 * and typed in src/types/prioritization/loopDiff.ts. These helpers only turn
 * its fields into plain words for the three places that show it.
 */

import type { LoopDiff, LoopDiffRankingRow } from '../../types/prioritization/loopDiff';

const REQUEST_FIELD_LABELS: Record<string, string> = {
  asks: 'What was asked',
  constraints: 'Constraints',
  guardrails: 'Guardrails',
  evidenceExpectations: 'Evidence expected',
  strategicContext: 'Context',
  dueDate: 'Due date',
  responseGuidance: 'Response guidance',
  expectedResponseType: 'Expected response',
  interpretation: 'Interpretation',
  proposalCountGuidance: 'How many proposals',
  imposedCriteria: 'Imposed criteria',
};

/** Plain words for a field of the request. */
export function requestFieldLabel(field: string): string {
  return REQUEST_FIELD_LABELS[field] ?? field;
}

/** "Rose 5, above the line now" — what happened to one item between the two passes. */
export function rankingChangeLabel(row: LoopDiffRankingRow): string {
  const base =
    row.change === 'rose' ? `Rose ${row.places ?? ''}`.trim()
    : row.change === 'fell' ? `Fell ${row.places ?? ''}`.trim()
    : row.change === 'entered' ? 'Entered'
    : row.change === 'left' ? 'Left'
    : 'Same place';
  if (row.crossed === 'up') return `${base}, above the line now`;
  if (row.crossed === 'down') return `${base}, below the line now`;
  return base;
}

/** Only the rows a reader needs to look at: anything that is not in the same place. */
export function changedRankingRows(diff: LoopDiff): LoopDiffRankingRow[] {
  return (diff.ranking?.rows ?? []).filter((row) => row.change !== 'same');
}

/** The block has something to say when there is a reference pass to compare with. */
export function loopDiffHasReference(diff: LoopDiff | null | undefined): diff is LoopDiff {
  return Boolean(diff && diff.success && diff.reference);
}

/** "Compared with turn 1, returned by Dana R on Jul 6, 2026" */
export function referenceLine(diff: LoopDiff): string {
  const ref = diff.reference;
  if (!ref) return '';
  const who = ref.decided_by_name ? ` by ${ref.decided_by_name}` : '';
  const when = ref.decided_at ? ` on ${formatDay(ref.decided_at)}` : '';
  if (ref.kind === 'plan') return `Compared with ${ref.label}${who}`;
  return `Compared with ${ref.label}, ${ref.decision_word}${who}${when}`;
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
